import React, { Suspense, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { preloadService } from './services/preloadService';
import WebVitalsOptimizer from './components/WebVitalsOptimizer';
import './i18n';
// Import iOS Safari polyfills
import './utils/iosPolyfills';


import { queryClient } from './lib/react-query';
import { analytics } from './utils/analytics';
import ErrorBoundary from './components/ErrorBoundary';
import LoadingSpinner from './components/LoadingSpinner';
import Navbar from './components/Navbar';
import AnnouncementBar from './components/AnnouncementBar';
import PopupHost from './components/PopupHost';
import Footer from './components/Footer';
import EcommerceApi from './pages/EcommerceApi';
import DownloadCenter from './pages/DownloadCenter';
import Contact from './components/Contact';
import { ChatWidget } from './components/Chatbot/ChatWidget';


// Clear old localStorage user data when switching to backend
// Verify backend integration is working
import NotFound from './components/NotFound';
import PersonalCurrentAccount from './pages/PersonalCurrentAccount';
import StudentCurrentAccount from './pages/StudentCurrentAccount';
import JointAccount from './pages/JointAccount';
import VipCurrentAccount from './pages/VipCurrentAccount';
import BanksList from './pages/BanksList';
import SocietyAccount from './pages/SocietyAccount';
import SmeCorporationAccount from './pages/SmeCorporationAccount';
import OtherBusinessTypesAccount from './pages/OtherBusinessTypesAccount';

// Lazy load all pages
const Home = React.lazy(() => import('./pages/Home'));
const About = React.lazy(() => import('./pages/About'));
const AgentBanking = React.lazy(() => import('./pages/AgentBanking'));
const AtmServices = React.lazy(() => import('./pages/AtmServices'));
const BillerAccount = React.lazy(() => import('./pages/BillerAccount'));
const BoardOfDirectors = React.lazy(() => import('./pages/BoardOfDirectors'));
const Brochures = React.lazy(() => import('./pages/Brochures'));
const Careers = React.lazy(() => import('./pages/Careers'));
const ConstructionFinancing = React.lazy(() => import('./pages/ConstructionFinancing'));
const DebitCard = React.lazy(() => import('./pages/DebitCard'));
const DiasporaBanking = React.lazy(() => import('./pages/DiasporaBanking'));
const ElectronicsFinancing = React.lazy(() => import('./pages/ElectronicsFinancing'));
const EmailBanking = React.lazy(() => import('./pages/EmailBanking'));
const FAQs = React.lazy(() => import('./pages/FAQs'));
const FixedDeposit = React.lazy(() => import('./pages/FixedDeposit'));
const ForeignExchange = React.lazy(() => import('./pages/ForeignExchange'));
const FurnitureFinancing = React.lazy(() => import('./pages/FurnitureFinancing'));
const HomeFinancing = React.lazy(() => import('./pages/HomeFinancing'));
const InternetMobileBanking = React.lazy(() => import('./pages/InternetMobileBanking'));
const Microfinance = React.lazy(() => import('./pages/Microfinance'));
const News = React.lazy(() => import('./pages/News'));
const NewsDetails = React.lazy(() => import('./pages/NewsDetails'));
const Gallery = React.lazy(() => import('./pages/Gallery'));
const GalleryAlbum = React.lazy(() => import('./pages/GalleryAlbum'));

const NgosAccount = React.lazy(() => import('./pages/NgosAccount'));
const OnlineBillPayments = React.lazy(() => import('./pages/OnlineBillPayments'));
const PosServices = React.lazy(() => import('./pages/PosServices'));
const PropertyFinancing = React.lazy(() => import('./pages/PropertyFinancing'));
const SavingsAccount = React.lazy(() => import('./pages/SavingsAccount'));
const SchoolsAccount = React.lazy(() => import('./pages/SchoolsAccount'));
const SelfOnboarding = React.lazy(() => import('./pages/SelfOnboarding'));
const ServiceDetails = React.lazy(() => import('./pages/ServiceDetails'));
const Services = React.lazy(() => import('./pages/Services'));
const SmsNotifications = React.lazy(() => import('./pages/SmsNotifications'));
const SoleProprietor = React.lazy(() => import('./pages/SoleProprietor'));
const SwiftTransfers = React.lazy(() => import('./pages/SwiftTransfers'));
const TradeFinancing = React.lazy(() => import('./pages/TradeFinancing'));
const VehicleFinancing = React.lazy(() => import('./pages/VehicleFinancing'));
const WhatDrivesUs = React.lazy(() => import('./pages/WhatDrivesUs'));
const WhoWeAre = React.lazy(() => import('./pages/WhoWeAre'));
const DownloadApp = React.lazy(() => import('./components/DownloadApp'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsAndConditions = React.lazy(() => import('./pages/TermsAndConditions'));
const Disclaimer = React.lazy(() => import('./pages/Disclaimer'));
const OtherServices = React.lazy(() => import('./pages/OtherServices'));
const EvcPlusIntegration = React.lazy(() => import('./pages/EvcPlusIntegration'));
const InternationalizationDemo = React.lazy(() => import('./pages/InternationalizationDemo'));
const CustomerSurvey = React.lazy(() => import('./pages/CustomerSurvey'));
interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => (
  <ErrorBoundary>
    <div className="flex flex-col min-h-screen">
      <AnnouncementBar />
      <Navbar />
      <main className="flex-grow">
        {children}
      </main>
      <DownloadApp />
      <Footer />
      <PopupHost />
      <ChatWidget />
    </div>
  </ErrorBoundary>
);

const App: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();

  // Initialize performance optimizations
  useEffect(() => {
    preloadService.initializePerformanceOptimizations();
    preloadService.monitorWebVitals();

    // Preload homepage resources if on homepage
    if (location.pathname === '/') {
      preloadService.preloadHomepageResources();
    }

    // Lazy load non-critical resources
    preloadService.lazyLoadNonCriticalResources();
  }, []);

  // Initialize analytics
  useEffect(() => {
    const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
    if (GA_MEASUREMENT_ID) {
      analytics.init(GA_MEASUREMENT_ID);
    }
  }, []);

  // Track page views
  useEffect(() => {
    analytics.trackPageView(location.pathname + location.search);
  }, [location]);

  // Scroll to top on route change (path changes)
  useEffect(() => {
    // Use smooth behavior for nicer UX
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);


  // Register service worker
  // React.useEffect(() => {
  //   if ('serviceWorker' in navigator) {
  //     window.addEventListener('load', () => {
  //       navigator.serviceWorker.register('/service-worker.js')
  //         .then(registration => {
  //           console.log('SW registered: ', registration);
  //         })
  //         .catch(registrationError => {
  //           console.log('SW registration failed: ', registrationError);
  //         });
  //     });
  //   }
  // }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <WebVitalsOptimizer>
          <QueryClientProvider client={queryClient}>
            <div className="App">
              <Toaster
                position="top-right"
                toastOptions={{
                  className: 'dark:bg-gray-800 dark:text-white'
                }}
              />
              <Helmet>
                <title>{t('meta.title')}</title>
                <meta name="description" content={t('meta.description')} />
              </Helmet>
              <Routes>
                <Route path="/so/*" element={<LanguageSync lang="so"><AllAppRoutes /></LanguageSync>} />
                <Route path="/*" element={<LanguageSync lang="en"><AllAppRoutes /></LanguageSync>} />
              </Routes>

            </div>

          </QueryClientProvider>
        </WebVitalsOptimizer>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;

/**
 * Ensures i18next language stays in sync with the route prefix
 */
const LanguageSync: React.FC<{ lang: string; children: React.ReactNode }> = ({ lang, children }) => {
  const { i18n } = useTranslation();

  React.useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  return <>{children}</>;
};

/**
 * All application routes consolidated for dual-mounting
 */
const AllAppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <Home />
          </Suspense>
        </Layout>
      } />
      <Route path="/about" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <About />
          </Suspense>
        </Layout>
      } />
      <Route path="/who-we-are" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <WhoWeAre />
          </Suspense>
        </Layout>
      } />
      <Route path="/what-drives-us" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <WhatDrivesUs />
          </Suspense>
        </Layout>
      } />
      <Route path="/board-of-directors" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <BoardOfDirectors isAboutPage={false} />
          </Suspense>
        </Layout>
      } />
      <Route path="/careers" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <Careers />
          </Suspense>
        </Layout>
      } />
      <Route path="/faqs" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <FAQs />
          </Suspense>
        </Layout>
      } />
      {/* Legacy URLs hard-coded in the mobile app (cannot be updated). Served in
          place with the same content; canonical -> /who-we-are and /faqs. */}
      <Route path="/about-us/who-we-are" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <WhoWeAre />
          </Suspense>
        </Layout>
      } />
      <Route path="/about-us/mybank-faqs" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <FAQs />
          </Suspense>
        </Layout>
      } />
      <Route path="/contact-us" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <Contact isHomePage={false} />
          </Suspense>
        </Layout>
      } />

      <Route path="/services" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <Services />
          </Suspense>
        </Layout>
      } />
      <Route path="/services/:slug" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <ServiceDetails />
          </Suspense>
        </Layout>
      } />
      <Route path="/news" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <News />
          </Suspense>
        </Layout>
      } />
      <Route path="/news/:id" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <NewsDetails />
          </Suspense>
        </Layout>
      } />
      <Route path="/gallery" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <Gallery />
          </Suspense>
        </Layout>
      } />
      <Route path="/gallery/:slug" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <GalleryAlbum />
          </Suspense>
        </Layout>
      } />

      <Route path="/savings-account" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <SavingsAccount />
          </Suspense>
        </Layout>
      } />
      <Route path="/personal-current-account" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <PersonalCurrentAccount />
          </Suspense>
        </Layout>
      } />
      <Route path="/student-current-account" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <StudentCurrentAccount />
          </Suspense>
        </Layout>
      } />
      <Route path="/joint-account" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <JointAccount />
          </Suspense>
        </Layout>
      } />
      <Route path="/vip-current-account" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <VipCurrentAccount />
          </Suspense>
        </Layout>
      } />

      <Route path="/fixed-deposit" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <FixedDeposit />
          </Suspense>
        </Layout>
      } />
      <Route path="/vehicle-financing" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <VehicleFinancing />
          </Suspense>
        </Layout>
      } />
      <Route path="/home-financing" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <HomeFinancing />
          </Suspense>
        </Layout>
      } />
      <Route path="/electronics-financing" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <ElectronicsFinancing />
          </Suspense>
        </Layout>
      } />
      <Route path="/furniture-financing" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <FurnitureFinancing />
          </Suspense>
        </Layout>
      } />
      <Route path="/microfinance" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <Microfinance />
          </Suspense>
        </Layout>
      } />
      <Route path="/debit-card" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <DebitCard />
          </Suspense>
        </Layout>
      } />
      <Route path="/forex" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <ForeignExchange />
          </Suspense>
        </Layout>
      } />
      <Route path="/swift" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <SwiftTransfers />
          </Suspense>
        </Layout>
      } />
      <Route path="/bill-payments" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <OnlineBillPayments />
          </Suspense>
        </Layout>
      } />
      <Route path="/atm-services" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <AtmServices />
          </Suspense>
        </Layout>
      } />
      <Route path="/pos" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <PosServices />
          </Suspense>
        </Layout>
      } />
      <Route path="/internet-and-mobile-banking" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <InternetMobileBanking />
          </Suspense>
        </Layout>
      } />
      <Route path="/sms" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <SmsNotifications />
          </Suspense>
        </Layout>
      } />
      <Route path="/agents" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <AgentBanking />
          </Suspense>
        </Layout>
      } />
      <Route path="/email-banking" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <EmailBanking />
          </Suspense>
        </Layout>
      } />
      <Route path="/sole-proprietor-account" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <SoleProprietor />
          </Suspense>
        </Layout>
      } />
      <Route path="/other-business-types" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <OtherBusinessTypesAccount />
          </Suspense>
        </Layout>
      } />
      <Route path="/biller-account" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <BillerAccount />
          </Suspense>
        </Layout>
      } />
      <Route path="/education-account" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <SchoolsAccount />
          </Suspense>
        </Layout>
      } />
      <Route path="/ngo-account" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <NgosAccount />
          </Suspense>
        </Layout>
      } />
      <Route path="/society-account" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <SocietyAccount />
          </Suspense>
        </Layout>
      } />
      <Route path="/sme-corporation" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <SmeCorporationAccount />
          </Suspense>
        </Layout>
      } />
      <Route path="/construction-financing" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <ConstructionFinancing />
          </Suspense>
        </Layout>
      } />
      <Route path="/trade-financing" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <TradeFinancing />
          </Suspense>
        </Layout>
      } />
      <Route path="/property-financing" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <PropertyFinancing />
          </Suspense>
        </Layout>
      } />
      <Route path="/diaspora-banking" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <DiasporaBanking />
          </Suspense>
        </Layout>
      } />
      <Route path="/open-account" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <SelfOnboarding />
          </Suspense>
        </Layout>
      } />
      <Route path="/ecommerce-api" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <EcommerceApi />
          </Suspense>
        </Layout>
      } />
      <Route path="/download-center" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <DownloadCenter />
          </Suspense>
        </Layout>
      } />
      <Route path="/brochures" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <Brochures />
          </Suspense>
        </Layout>
      } />
      <Route path="/privacy-policy" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <PrivacyPolicy />
          </Suspense>
        </Layout>
      } />
      <Route path="/terms" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <TermsAndConditions />
          </Suspense>
        </Layout>
      } />
      <Route path="/disclaimer" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <Disclaimer />
          </Suspense>
        </Layout>
      } />
      <Route path="/other-services" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <OtherServices />
          </Suspense>
        </Layout>
      } />
      <Route path="/banks" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <BanksList />
          </Suspense>
        </Layout>
      } />
      <Route path="/evc-plus-integration" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <EvcPlusIntegration />
          </Suspense>
        </Layout>
      } />
      <Route path="/i18n-demo" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <InternationalizationDemo />
          </Suspense>
        </Layout>
      } />
      <Route path="/customer-survey" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <CustomerSurvey />
          </Suspense>
        </Layout>
      } />

      {/* Catch-all route for non-existent pages */}
      <Route path="*" element={
        <Layout>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><LoadingSpinner /></div>}>
            <NotFound />
          </Suspense>
        </Layout>
      } />
    </Routes>
  );
};
